import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdArrowBack,
  MdArrowForward,
  MdCheck,
  MdMyLocation,
  MdNavigation,
} from "react-icons/md";

import StopsMap, { MAP_BUTTON } from "./StopsMap";
import { metersText } from "./driverLocation";
import { mapsApi, mapStops } from "./routeMapStops";
import { navigateUrl } from "./routeSheet";
import { routeLine, straightMeters } from "./runDistance";

// Waze opens its app at an address and starts the drive there.
export const wazeUrl = (address) =>
  `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;

// What the location chip in the header says.
const LOCATION_WORDS = {
  on: "Location on",
  waiting: "Finding location",
  denied: "Location blocked",
  unavailable: "No location",
  off: "Location off",
};

/**
 * The first stop still to do at or after `fromId` in the run's order (then
 * from the top), or null when every stop is done.
 */
export const nextStopId = (stops, done, fromId = null) => {
  const ids = stops.map((stop) => stop.id);
  const from = Math.max(0, ids.indexOf(fromId));
  const order = [...ids.slice(from), ...ids.slice(0, from)];
  return order.find((id) => !done.has(id)) ?? null;
};

// The screen stays on while the driver view is open (where the browser
// lets it): the position keeps coming and the next stop stays in sight.
const useWakeLock = () => {
  useEffect(() => {
    let lock = null;
    let closed = false;
    const ask = async () => {
      try {
        if (document.visibilityState === "visible" && navigator.wakeLock) {
          lock = await navigator.wakeLock.request("screen");
          if (closed) lock?.release?.();
        }
      } catch {
        lock = null;
      }
    };
    ask();
    // The browser lets the lock go when the page is hidden: asked again.
    const again = () => {
      if (!closed) ask();
    };
    document.addEventListener("visibilitychange", again);
    return () => {
      closed = true;
      document.removeEventListener("visibilitychange", again);
      lock?.release?.()?.catch?.(() => {});
    };
  }, []);
};

/**
 * The driver view: the run on the whole screen for the driver on the road.
 * The map (when Google's interactive map is there) shows the route, every
 * stop by number, where each recorded stop was, and the phone's position as
 * it moves (`fix`, from the delivery list's location; Center on me goes
 * there). Under it, the stops in a strip (done ones ticked) and the current
 * stop: how far it is, Google Maps and Waze to drive there with their voice
 * directions, and its delivery list card (`renderStop(orderId)`) to record
 * it. When the current stop is done, Next stop goes to the next one still to
 * do. `stops` are the sheet's orders in order, `start` ({ name, address })
 * where the run starts, `places` where Google put each address (address →
 * { lat, lng }), `points` the drive's addresses (for its route line, the
 * request the sheet already made), `done` the order ids recorded or
 * fulfilled, `location` the sharing state ("on", "waiting", "off"...) and
 * `onShare()` turns sharing on. Esc or Back closes it (`onClose`).
 */
const DriverView = ({
  title = "",
  stops = [],
  start = null,
  places = new Map(),
  points = [],
  done = new Set(),
  fix = null,
  location = "off",
  onShare,
  renderStop,
  spots = {},
  onClose,
}) => {
  useWakeLock();
  const titleId = useId();
  const backRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [maps] = useState(mapsApi);

  // Esc closes it; the page underneath doesn't hear it.
  useEffect(() => {
    backRef.current?.focus();
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const all = useMemo(() => mapStops(stops), [stops]);
  const placed = all.map((stop) => ({
    ...stop,
    place: (stop.address && places.get(stop.address)) || null,
  }));
  const startMark = start?.address
    ? { ...start, place: places.get(start.address) || null }
    : null;

  // The road route, from the drive the sheet asked for already.
  const pointsKey = points.join("\n");
  const [path, setPath] = useState(null);
  useEffect(() => {
    if (!maps || points.length < 2) return undefined;
    let cancelled = false;
    routeLine(points, maps).then((line) => {
      if (!cancelled) setPath(line?.path ?? null);
    });
    return () => {
      cancelled = true;
    };
    // The points are the key's parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps, pointsKey]);

  const [currentId, setCurrentId] = useState(() => nextStopId(all, done));
  const current = placed.find((stop) => stop.id === currentId) ?? null;
  const currentDone = current ? done.has(current.id) : false;
  const nextId = current ? nextStopId(all, done, current.id) : null;
  const doneCount = all.filter((stop) => done.has(stop.id)).length;

  // The map follows the current stop (picked in the strip or on the map)
  // and, on Center on me, the phone.
  const seq = useRef(0);
  const [selection, setSelection] = useState(() =>
    currentId == null ? null : { id: currentId, how: "list", seq: 0 },
  );
  const goTo = (id, how = "list") => {
    if (id == null) return;
    seq.current += 1;
    setCurrentId(id);
    setSelection({ id, how, seq: seq.current });
  };
  const [focus, setFocus] = useState(null);
  const me = fix ? { lat: fix.lat, lng: fix.lng } : null;
  const away =
    me && current?.place ? metersText(straightMeters(me, current.place)) : "";

  const fitKey = [
    startMark?.place ? start.address : "",
    ...placed
      .filter((stop) => stop.place)
      .map((stop) => stop.address)
      .sort(),
  ].join("\n");
  const drops = placed
    .filter((stop) => spots[stop.id])
    .map((stop) => ({
      id: stop.id,
      number: stop.number,
      place: spots[stop.id].place,
      distance: spots[stop.id].distance,
      far: spots[stop.id].far,
      address: stop.place,
    }));

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-[1350] flex flex-col bg-ui-page font-manrope text-[12px] text-ui-text"
    >
      <header className="flex shrink-0 items-center gap-[10px] border-b-[1px] border-ui-border bg-ui-surface px-[12px] pb-[8px] pt-[calc(8px+env(safe-area-inset-top,0px))]">
        <button
          ref={backRef}
          type="button"
          onClick={onClose}
          aria-label="Back to the delivery list"
          className="grid h-[40px] w-[40px] shrink-0 place-items-center rounded-[8px] border-[1px] border-ui-border bg-ui-surface text-ui-text hover:border-brand hover:text-brand"
        >
          <MdArrowBack className="h-[20px] w-[20px]" />
        </button>
        <div className="grid min-w-[0px] flex-1 gap-[1px]">
          <h2
            id={titleId}
            className="m-[0px] truncate font-jakarta text-[15px] font-extrabold leading-[1.25] text-ui-text"
          >
            Driver view{title ? ` · ${title}` : ""}
          </h2>
          <span className="truncate text-[11.5px] text-ui-text-muted">
            {doneCount} of {all.length} stops done
          </span>
        </div>
        {location === "off" && onShare ? (
          <button
            type="button"
            onClick={onShare}
            className="flex min-h-[36px] shrink-0 items-center gap-[5px] rounded-[7px] border-[1px] border-brand px-[9px] text-[12px] font-semibold text-brand"
          >
            <MdMyLocation className="h-[15px] w-[15px]" />
            Share location
          </button>
        ) : (
          <span
            className={`shrink-0 rounded-[4px] border-[1px] px-[7px] py-[2px] text-[11px] font-semibold ${
              location === "on"
                ? "border-state-success-border bg-state-success-bg text-state-success-text"
                : "border-state-neutral-border bg-state-neutral-bg text-state-neutral-text"
            }`}
          >
            {LOCATION_WORDS[location] ?? LOCATION_WORDS.off}
          </span>
        )}
      </header>

      <div className="flex min-h-[0px] flex-1 flex-col md:flex-row">
        {maps ? (
          <StopsMap
            maps={maps}
            start={startMark}
            stops={placed}
            count={all.length}
            path={path}
            fitKey={fitKey}
            selection={selection}
            onSelect={(id) => {
              if (id != null && id !== "start") goTo(id, "map");
            }}
            cardless
            focus={focus}
            gestureHandling="greedy"
            drops={drops}
            driver={me ? { place: me, self: true } : null}
            tools={
              me && (
                <button
                  type="button"
                  onClick={() => setFocus({ place: me, seq: Date.now() })}
                  className={MAP_BUTTON}
                >
                  <MdMyLocation className="h-[16px] w-[16px]" />
                  Center on me
                </button>
              )
            }
            className="min-h-[200px] flex-1 md:order-2"
          />
        ) : null}

        <section
          aria-label="Current stop"
          className={`flex shrink-0 flex-col border-t-[1px] border-ui-border bg-ui-surface md:order-1 md:w-[400px] md:border-r-[1px] md:border-t-0 ${
            maps ? "max-h-[58%] md:max-h-[none]" : "flex-1"
          }`}
        >
          <nav
            aria-label="Stops"
            className="flex shrink-0 gap-[6px] overflow-x-auto border-b-[1px] border-ui-border-subtle px-[10px] py-[8px]"
          >
            {all.map((stop) => {
              const isCurrent = stop.id === currentId;
              const isDone = done.has(stop.id);
              return (
                <button
                  key={stop.id}
                  type="button"
                  aria-current={isCurrent ? "true" : undefined}
                  aria-label={`Stop ${stop.number}${stop.customer ? `, ${stop.customer}` : ""}${isDone ? ", done" : ""}`}
                  onClick={() => goTo(stop.id)}
                  className={`flex h-[36px] min-w-[36px] shrink-0 items-center justify-center gap-[3px] rounded-full border-[2px] px-[8px] text-[13px] font-extrabold ${
                    isCurrent
                      ? "border-brand bg-brand text-white"
                      : isDone
                        ? "border-state-success-border bg-state-success-bg text-state-success-text"
                        : "border-ui-border bg-ui-surface text-ui-text"
                  }`}
                >
                  {isDone && (
                    <MdCheck aria-hidden="true" className="h-[13px] w-[13px]" />
                  )}
                  {stop.number}
                </button>
              );
            })}
          </nav>
          <div className="min-h-[0px] flex-1 overflow-y-auto p-[10px] pb-[calc(10px+env(safe-area-inset-bottom,0px))]">
            {current ? (
              <div className="grid gap-[8px]">
                <div className="flex flex-wrap items-center gap-[8px]">
                  <span className="text-[13px] font-extrabold">
                    Stop {current.number} of {all.length}
                  </span>
                  {away && (
                    <span className="text-[12px] text-ui-text-muted">
                      {away} from you
                    </span>
                  )}
                  {currentDone && (
                    <span className="rounded-[4px] border-[1px] border-state-success-border bg-state-success-bg px-[6px] py-[1px] text-[11px] font-semibold text-state-success-text">
                      Done
                    </span>
                  )}
                </div>
                {currentDone && nextId != null && nextId !== current.id && (
                  <button
                    type="button"
                    onClick={() => goTo(nextId)}
                    className="flex min-h-[44px] items-center justify-center gap-[6px] rounded-[8px] border-[1px] border-brand bg-brand px-[12px] text-[14px] font-bold text-white"
                  >
                    Next stop:{" "}
                    {placed.find((stop) => stop.id === nextId)?.customer ||
                      `Stop ${placed.find((stop) => stop.id === nextId)?.number}`}
                    <MdArrowForward
                      aria-hidden="true"
                      className="h-[18px] w-[18px]"
                    />
                  </button>
                )}
                {current.address && !currentDone && (
                  <div className="grid grid-cols-2 gap-[8px]">
                    <a
                      href={navigateUrl(current.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex min-h-[44px] items-center justify-center gap-[6px] rounded-[8px] border-[1px] border-brand bg-brand px-[10px] text-[13.5px] font-bold text-white no-underline hover:bg-brand-hover hover:text-white hover:no-underline"
                    >
                      <MdNavigation
                        aria-hidden="true"
                        className="h-[17px] w-[17px]"
                      />
                      Google Maps
                    </a>
                    <a
                      href={wazeUrl(current.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex min-h-[44px] items-center justify-center gap-[6px] rounded-[8px] border-[1px] border-ui-border-strong bg-ui-surface px-[10px] text-[13.5px] font-bold text-ui-text no-underline hover:border-brand hover:text-brand hover:no-underline"
                    >
                      <MdNavigation
                        aria-hidden="true"
                        className="h-[17px] w-[17px]"
                      />
                      Waze
                    </a>
                  </div>
                )}
                {renderStop?.(current.id)}
              </div>
            ) : (
              <p className="m-[0px] p-[12px] text-center text-[13px] font-semibold text-state-success-text">
                Every stop on this run is done.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>,
    document.body,
  );
};

export default DriverView;
